/**
 * advance-outstanding.ts — what is still owed on the contract's operational advances.
 *
 * THE OVERVIEW'S "ACTIVE ADVANCES" TILE TOOK OFF THE WRONG MONEY. It subtracted only the
 * repayments recorded straight against an advance (`repayments_total`, the `loan_repayments`
 * rows) and then added every advance's interest back on top. Advances are not repaid that way:
 * the weekly payment is split across them by the allocator, and that share arrives as
 * `allocated_total`, which the tile never read. Production holds one direct repayment against
 * 163 advances, so the tile showed each advance's disbursement plus its interest, in full, for
 * ever — 529,634.50 across the book where 223,170.42 was owed, and a debt on nine running
 * contracts that owe nothing at all.
 *
 * ── THE MONEY IS THE ADVANCES TAB'S, NOT A NEW FORMULA ───────────────────────
 * The Advances tab has always counted both kinds of money, and so does fleet-admin's twin of it.
 * Per advance (`ContractInsightLoans.tsx:97-101`):
 *
 *     repayable = amount + amount × rate / 100
 *     repaid    = allocated_total + repayments_total
 *     owed      = repayable − repaid, read to the millionth of a cedi, and nothing below zero
 *
 * and the engine caps an advance at the same repayable (`calculate_repayment_allocations` sets
 * `total_owed = amount + amount * rate / 100`), so an advance's allocations never run past it.
 * `allocated_total` carries the weekly split and the tail credits an advance with no weekly
 * receives once the vehicle is paid for [D-B58] alike.
 *
 * ── "NONE" IS DECIDED ON THE CONTRACT'S TOTAL ────────────────────────────────
 * The tile prints a count beside the figure and "None" when the count is zero, and both come from
 * one decision over the contract's advances together. When they owe at least 0.005 between them —
 * half a pesewa, the least total that prints as 0.01 — every advance that owes anything is counted
 * and the figure is what those advances, and no others, owe. Below it, the count and the figure are
 * both zero. So "None" means the advances owe less than half a pesewa altogether, an amount that
 * prints as 0.00, and a count above zero always stands beside at least 0.01. It does not mean
 * nothing at all is owed: one advance owing 0.004 reads "None", since it could only print as
 * "1 (0.00 outstanding)".
 *
 * THE LINE IS NOT DRAWN PER ADVANCE. Dropping each remainder under half a pesewa let two advances
 * owing 0.004 each print "None" over 0.008, which prints as 0.01. The count is every advance with a
 * remainder, not every advance at or above half a pesewa: the figure sums those remainders, so a
 * count of the larger ones alone would print "0 (0.01 outstanding)" there.
 *
 * EVERY REMAINDER IS READ TO THE MILLIONTH OF A CEDI before it is counted, summed or held to the
 * line, and so is their sum. A remainder is the difference of sums built in binary floating point
 * and carries their noise. An advance the allocator has closed reads 4e-11 rather than nothing
 * (3,279.99999999996 of 3,280), and of four remainders that are each exactly half a pesewa,
 * 2,400 − 2,399.995 and 5,000 − 4,999.995 compute as 0.0050000000001 while 1,000 − 999.995 and
 * 100 − 99.995 compute as 0.0049999999999955: compared raw, two printed "1 (0.01 outstanding)" and
 * two printed "None". Read to the millionth, the dust is nothing and the four are one 0.005, which
 * prints as 0.01. A millionth is a ten-thousandth of a pesewa, and the noise on production is under
 * a thousandth of a millionth (2e-10 at most, either way). Each reading moves an amount by at most
 * half a millionth, so a contract's figure moves by at most that for each advance and once more for
 * the sum: it changes a printed figure only for an amount that close to a half-pesewa line, which is
 * where that noise lands.
 *
 * THE TILE CAN DISAGREE WITH THE TAB'S "PAID OFF" BADGE INSIDE THE LAST 0.1%, ON PURPOSE. The badge
 * calls an advance paid off at repaid ≥ repayable × 0.999 (`ContractInsightLoans.tsx:101`); this
 * does not use that test. The two answer different questions: the badge says the advance is
 * substantially complete, the tile says money is still owed, and a figure too low is worse than one
 * too high (owner, 2026-11-02: never understate). The band is real. On contract 98, after the
 * 2025-09-23 payment, advance 161 stood at 2,398.46 of 2,400 (99.94%) and advance 117 was repaid:
 * a count on the badge's test would have read "None" while 1.54 was owed, which the allocator
 * collected on 2025-09-29. This module reads that day as "1 (1.54 outstanding)", while the tab
 * showed 161 Paid Off.
 *
 * The lifetime principal and interest the tile prints underneath are NOT this module's business:
 * they are totals over every advance the contract has had, the value chart's Advances slice reads
 * the same interest sum, and both stay as they were.
 */

/** An operational advance's money, to the depth this reads. */
export interface AdvanceMoneyShape {
    /** The amount disbursed. */
    amount: number;
    /** Simple interest on the whole term, as a percentage. NULL is no interest. */
    interest_rate?: number | null;
    /** `SUM(loan_repayments)` — what was paid straight at the advance. */
    repayments_total?: number | null;
    /** The allocator's share of the weekly payments booked to this advance. */
    allocated_total?: number | null;
}

/**
 * Half a pesewa: the least the contract's advances can owe between them and the tile still name a
 * debt. A total at or above it prints as at least 0.01; one below it prints as 0.00, and the tile
 * reads "None".
 */
export const LEAST_OWED = 0.005;

/**
 * An amount read to the millionth of a cedi, so the noise of binary floating point reads as what the
 * amount is: 4e-11 as nothing, 0.0049999999999955 and 0.0050000000001 as 0.005.
 */
function toTheMillionth(amount: number): number {
    return Math.round(amount * 1e6) / 1e6;
}

/** The advance plus its interest: everything the driver repays on it. */
export function advanceRepayable(advance: AdvanceMoneyShape): number {
    return advance.amount + (advance.amount * (advance.interest_rate || 0) / 100);
}

/** What has reached the advance, by either route. */
export function advanceRepaid(advance: AdvanceMoneyShape): number {
    return (advance.allocated_total || 0) + (advance.repayments_total || 0);
}

/**
 * What is still owed on one advance, read to the millionth of a cedi: its remainder, or nothing when
 * it has none — float dust on a closed advance, or an advance repaid past its total, which is never a
 * negative debt. A remainder under half a pesewa is returned as it is: whether the tile names it is
 * decided on the contract's total, in `activeAdvances`.
 */
export function advanceOutstanding(advance: AdvanceMoneyShape): number {
    const remainder = toTheMillionth(advanceRepayable(advance) - advanceRepaid(advance));
    return remainder > 0 ? remainder : 0;
}

export interface ActiveAdvances {
    /** The advances that still owe money. Zero is the tile's "None", and then `outstanding` is 0. */
    count: number;
    /** What they owe between them, read to the millionth of a cedi: 0, or at least `LEAST_OWED`. */
    outstanding: number;
}

/**
 * The Active Advances tile: how many advances still owe money, and what they owe. Every advance with
 * a remainder is counted and summed; when the sum is under half a pesewa, both are zero.
 */
export function activeAdvances(advances: readonly AdvanceMoneyShape[] | null | undefined): ActiveAdvances {
    const owing = (advances || []).map(advanceOutstanding).filter((owed) => owed > 0);
    const outstanding = toTheMillionth(owing.reduce((sum, owed) => sum + owed, 0));
    return outstanding >= LEAST_OWED ? { count: owing.length, outstanding } : { count: 0, outstanding: 0 };
}
